import { Button } from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { userListContext } from 'BrokerTsLib/src/context'
import type { UserGridItem, UserPagedQuery } from 'BrokerTsLib/src/dtos'
import type { FlexGroupUser } from 'BrokerTsLib/src/dtos/FlexGroup/FlexGroupUser'
import { noop } from 'lodash'
import { useState } from 'react'

import { FormSection, FormSectionBody, FormSectionDescription, FormSectionHeader, FormSectionTitle } from '@/components/FormSection'
import { Trash } from '@/icons'

import {
  UserList,
  UserListEmailColumn,
  UserListNameColumn,
  UserListOrganizationColumn,
  UserListRolesColumn,
  UserListTypeColumn,
  UserListValidityColumn,
} from '../users/UserList'
import { UserListFilter } from '../users/UserListFilter'
import { AddFlexGroupUser } from './AddFlexGroupUser'

interface FlexGroupUserListSectionProps {
  title: string
  description: string
  flexGroupUsers: FlexGroupUser[]
  isLoading: boolean
  addUserAsync: (userId: string) => Promise<any>
  removeUserAsync: (userId: string) => Promise<any>
}

export const FlexGroupUserListSection = (
  {
    title,
    description,
    flexGroupUsers,
    isLoading,
    addUserAsync,
    removeUserAsync,
  }: FlexGroupUserListSectionProps
) => {
  const [filter, setFilter] = useState<UserPagedQuery>({
    showInvalid: false,
    pageIndex: 1,
  })

  const organizationsFilter = (item: UserGridItem) => {
    if (!item.valid && !filter.showInvalid) return false
    if (
      filter.name &&
      !(
        `${item.firstName} ${item.lastName}`
          ?.toLowerCase()
          .includes(filter.name.toLowerCase()) ||
        item.userName?.toLowerCase().includes(filter.name.toLowerCase())
      )
    )
      return false

    return true
  }

  const users = flexGroupUsers.map(x => x.user)

  const filteredItems = users.filter(organizationsFilter)

  return (
    <FormSection>
      <FormSectionHeader display='flex' flexDirection='column'>
        <FormSectionTitle>{title}</FormSectionTitle>

        <FormSectionDescription>
          {description}
        </FormSectionDescription>

        <AddFlexGroupUser
          onAddUser={addUserAsync}
          alreadyAdded={users} />
      </FormSectionHeader>

      <FormSectionBody>
        <userListContext.Provider
          value={{
            loading: !!isLoading,
            totalCount: users?.length ?? 0,
            currentPage: filter.pageIndex,
            pageSize: users?.length ?? 0,
            filter,
            setCurrentPage: noop,
            setPageSize: noop,
            updateFilter: setFilter,
            reload: noop,
          }}
        >
          <UserListFilter />
          <UserList
            users={filteredItems}
            columnsDefinition={[
              UserListNameColumn,
              UserListOrganizationColumn,
              UserListEmailColumn,
              UserListValidityColumn,
              UserListTypeColumn,
              UserListRolesColumn,
              {
                key: 'removeFromFlexGroup',
                name: '',
                cellExpr: (item) => (
                  <CustomTooltip label="Odebrat">
                    <Button
                      size="sm"
                      variant="ghost"
                      color="red.800"
                      ml="auto"
                      onClick={() => removeUserAsync(item.userId)}
                    >
                      <Trash />
                    </Button>
                  </CustomTooltip>
                ),
              },
            ]}
            sx={{ width: 'full' }}
          />
        </userListContext.Provider>
      </FormSectionBody>
    </FormSection>
  )
}
